import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FiCheck, FiTrash2, FiX } from "react-icons/fi";
import { graphqlRequest } from "../../../../utils/graphqlRequest";
import { toast } from "react-toastify";
import "../../styles/ContractorPayroll.css";

const GET_WORK_ENTRIES = `
  query ContractorWorkEntries(
    $startDate: String
    $endDate: String
    $contractorId: String
    $projectId: String
  ) {
    contractorWorkEntries(
      startDate: $startDate
      endDate: $endDate
      contractorId: $contractorId
      projectId: $projectId
    ) {
      id
      date
      status
      workDescription
      hours
      rate
      amount
      source
      contractorId
      projectId
      contractor {
        id
        firstName
        lastName
        payType
        perHourRate
      }
      project {
        id
        name
      }
    }
  }
`;

const GET_WORK_TRACKING = `
  query ContractorWorkTracking(
    $startDate: String
    $endDate: String
  ) {
    contractorWorkTracking(
      startDate: $startDate
      endDate: $endDate
    ) {
      contractors {
        contractorId
        contractorName
        payType
        payRate
        shifts {
          date
          duration
          hours
          approvalStatus
        }
      }
    }
  }
`;

const CREATE_WORK_ENTRY = `
  mutation CreateContractorWorkEntry($input: CreateContractorWorkEntryInput!) {
    createContractorWorkEntry(input: $input) {
      id
      date
      workDescription
      hours
      rate
      amount
      contractorId
      projectId
    }
  }
`;

const APPROVE_WORK_ENTRY = `
  mutation ApproveContractorWorkEntry($id: String!) {
    approveContractorWorkEntry(id: $id) {
      id
      status
    }
  }
`;

const DRAFT_WORK_ENTRY = `
  mutation DraftContractorWorkEntry($id: String!) {
    draftContractorWorkEntry(id: $id) {
      id
      status
    }
  }
`;

const REMOVE_WORK_ENTRY = `
  mutation RemoveContractorWorkEntry($id: String!) {
    removeContractorWorkEntry(id: $id) {
      id
    }
  }
`;

const CONTRACTOR_ROLES = ["SUPERVISOR", "SCHEDULER_DISPATCHER", "CONTRACTOR"];
const normalizeRole = (r) =>
  String(r || "").trim().toUpperCase().replace(/[/\s-]+/g, "_");
const normalizeWorkStatus = (status) => (
  String(status || "").toLowerCase() === "approved" ? "approved" : "draft"
);
const fmtCurrency = (n) => `$${Number(n ?? 0).toFixed(2)}`;
const fmtRate = (n, payType) => {
  const normalized = String(payType || "").toLowerCase().replace(/[_\s-]+/g, "-");
  const suffix = normalized.includes("project") ? "" : "/hr";
  return `${fmtCurrency(n)}${suffix}`;
};
const fmtDate = (d) => {
  if (!d) return "—";
  const dt = new Date(d);
  return Number.isNaN(dt.getTime()) ? d : dt.toLocaleDateString("en-AU", { day: "2-digit", month: "2-digit", year: "numeric" });
};

const dateToYMD = (date) => date.toISOString().split("T")[0];
const DEFAULT_LOOKBACK_DAYS = 30;

const EMPTY_FORM = {
  contractorId: "",
  projectId: "",
  date: "",
  workDescription: "",
  hours: "",
  rate: "",
  amount: "",
};

const computeFormAmount = (contractorPayType, projectId, hoursValue, rateValue) => {
  const normalizedPayType = String(contractorPayType || "").toLowerCase().replace(/[_\s-]+/g, "-");
  const isProjectPay = normalizedPayType.includes("project");
  const hours = parseFloat(hoursValue) || 0;
  const rate = parseFloat(rateValue) || 0;
  const hasProject = String(projectId || "").trim() !== "";
  if (hours > 0) return (hours * rate).toFixed(2);
  if (isProjectPay && hasProject) return rate.toFixed(2);
  return "0.00";
};

const WorkTracking = ({
  sharedUsers = [],
  sharedProjects = [],
  sharedLoading = false,
  sharedError = "",
}) => {
  const defaultStart = dateToYMD(new Date(Date.now() - DEFAULT_LOOKBACK_DAYS * 24 * 60 * 60 * 1000));
  const defaultEnd = dateToYMD(new Date());
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filters, setFilters] = useState({
    startDate: defaultStart,
    endDate: defaultEnd,
    contractorId: "",
    projectId: "",
  });
  const [appliedFilters, setAppliedFilters] = useState({
    startDate: defaultStart,
    endDate: defaultEnd,
  });
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [updatingEntryId, setUpdatingEntryId] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);

  const contractors = useMemo(
    () => (sharedUsers || []).filter((u) => u.roles?.some((r) => CONTRACTOR_ROLES.includes(normalizeRole(r)))),
    [sharedUsers]
  );
  const projects = useMemo(() => sharedProjects || [], [sharedProjects]);
  const contractorLookup = useMemo(
    () => new Map(contractors.map((c) => [c.id, `${c.firstName || ""} ${c.lastName || ""}`.trim() || "—"])),
    [contractors]
  );
  const loadEntries = useCallback(async () => {
    if (sharedLoading) {
      setLoading(true);
      return;
    }
    setLoading(true);
    setError(sharedError || "");
    try {
      const [workEntriesResult, workTrackingResult] = await Promise.allSettled([
        graphqlRequest(GET_WORK_ENTRIES, {
          startDate: appliedFilters.startDate,
          endDate: appliedFilters.endDate,
          contractorId: filters.contractorId || undefined,
          projectId: undefined,
        }),
        graphqlRequest(GET_WORK_TRACKING, {
          startDate: appliedFilters.startDate,
          endDate: appliedFilters.endDate,
        }),
      ]);
      if (workEntriesResult.status === "rejected") throw workEntriesResult.reason;
      const workEntriesData = workEntriesResult.value;
      const workTrackingData = workTrackingResult.status === "fulfilled" ? workTrackingResult.value : null;

      // contractorWorkEntries now returns BOTH manual entries and approved timesheet entries
      // merged into one list. Use this as the primary source of truth.
      const rows = workEntriesData?.contractorWorkEntries || [];

      const mapped = rows.map((r) => {
        const contractor = r.contractor || {};
        const contractorName = `${contractor.firstName || ""} ${contractor.lastName || ""}`.trim()
          || contractorLookup.get(r.contractorId)
          || "—";
        const hours = Number(r.hours || 0);
        const amount = Number(r.amount || 0);
        const rate = Number(r.rate || contractor.perHourRate || 0);
        const isTimesheetEntry = String(r.source || "").toLowerCase() === "timesheet";
        // Contractor's registered payType is the primary signal.
        // Only fall back to inferring from hours/amount when payType is not set.
        const registeredPayType = String(contractor.payType || "").toLowerCase().replace(/[_\s-]+/g, "-");
        const effectivePayType = registeredPayType
          || (hours > 0 ? "pay-per-hour" : (amount > 0 ? "pay-per-project" : "pay-per-hour"));
        const isProject = effectivePayType.includes("project");
        const totalAmount = amount || (isProject ? rate : hours * rate);
        return {
          id: r.id,
          date: r.date,
          contractorId: contractor.id || r.contractorId,
          contractorName,
          project: isTimesheetEntry ? "Timesheet" : (r.project?.name || projects.find((p) => p.id === r.projectId)?.name || "—"),
          workDescription: r.workDescription || (isTimesheetEntry ? "Timesheet Entry" : "General work"),
          hours,
          rate,
          payType: effectivePayType,
          totalAmount: Number(totalAmount) || 0,
          status: normalizeWorkStatus(r.status),
          isTimesheet: isTimesheetEntry,
        };
      });

      // Dedup tracking timesheet rows at the DATE level (not contractor level).
      // A contractor may have manual entries in contractorWorkEntries for some dates
      // AND separate timesheet entries in contractorWorkTracking for other dates.
      // We include tracking shifts for any (contractorId + date) not already covered
      // by contractorWorkEntries, so both sources contribute without duplication.
      const coveredKeys = new Set(
        mapped.map((row) => `${row.contractorId}|${row.date}`).filter(Boolean)
      );
      const trackingContractors = workTrackingData?.contractorWorkTracking?.contractors || [];
      const timesheetRows = trackingContractors
        .filter((c) => !filters.contractorId || c.contractorId === filters.contractorId)
        .flatMap((c) => {
          const normalizedPayType = String(c.payType || "").toLowerCase().replace(/[_\s-]+/g, "-");
          const rate = Number(c.payRate || 0);
          return (c.shifts || [])
            .filter((s) => s?.date && !coveredKeys.has(`${c.contractorId}|${s.date}`))
            .map((s, index) => {
              const hours = Number(s.hours || 0);
              // Registered payType is the primary signal. Only infer from hours when payType is not set.
              const shiftPayType = normalizedPayType || (hours > 0 ? "pay-per-hour" : "pay-per-project");
              const shiftIsProject = shiftPayType.includes("project");
              const totalAmount = Number((shiftIsProject ? rate : hours * rate).toFixed(2));
              const shiftPayTypeLabel = shiftIsProject ? "Pay per Project" : "Pay per Hour";
              return {
                id: `timesheet-${c.contractorId}-${s.date}-${index}`,
                date: s.date,
                contractorId: c.contractorId,
                contractorName: c.contractorName || contractorLookup.get(c.contractorId) || "—",
                project: "Timesheet",
                workDescription: s.duration
                  ? `Timesheet (${s.duration}) - ${shiftPayTypeLabel}`
                  : `Timesheet Entry - ${shiftPayTypeLabel}`,
                hours,
                rate,
                payType: shiftPayType,
                totalAmount,
                status: normalizeWorkStatus(s.approvalStatus || "approved"),
                isTimesheet: true,
              };
            });
        });

      const allRows = [...mapped, ...timesheetRows];
      setEntries(allRows.sort((a, b) => new Date(b.date) - new Date(a.date)));
    } catch (err) {
      setError(err?.message || "Failed to load contractor timesheet entries.");
    } finally {
      setLoading(false);
    }
  }, [contractorLookup, projects, appliedFilters.startDate, appliedFilters.endDate, filters.contractorId, sharedLoading, sharedError]);

  useEffect(() => {
    loadEntries();
  }, [loadEntries]);

  const contractorOptions = useMemo(
    () => contractors.map((c) => ({ id: c.id, name: `${c.firstName || ""} ${c.lastName || ""}`.trim() || "—" })),
    [contractors]
  );

  const displayEntries = useMemo(() => {
    const projectFilter = String(filters.projectId || "").toLowerCase().trim();
    return entries.filter((row) => {
      const contractorMatch = !filters.contractorId || row.contractorId === filters.contractorId;
      const projectMatch = !projectFilter || String(row.project || "").toLowerCase().includes(projectFilter);
      return contractorMatch && projectMatch;
    });
  }, [entries, filters.contractorId, filters.projectId]);
  const tableEntries = useMemo(
    () => (showModal
      ? displayEntries.filter((row) => !row.isTimesheet)
      : displayEntries.filter((row) => String(row.status || "").toLowerCase() === "approved")),
    [displayEntries, showModal]
  );
  const handleFormChange = (field, value) => {
    setForm((prev) => {
      const updated = { ...prev, [field]: value };
      if (field === "hours" || field === "rate" || field === "contractorId" || field === "projectId") {
        const selectedContractorId = field === "contractorId" ? value : updated.contractorId;
        const selectedContractor = contractors.find((c) => c.id === selectedContractorId);
        updated.amount = computeFormAmount(
          selectedContractor?.payType,
          updated.projectId,
          updated.hours,
          updated.rate
        );
      }
      return updated;
    });
  };

  const handleSave = async () => {
    if (saving) return;
    if (!form.contractorId || !form.projectId || !form.date) {
      toast.error("Contractor, Project and Date are required.");
      return;
    }
    if (!String(form.workDescription || "").trim()) {
      toast.error("Work type is required.");
      return;
    }

    try {
      setSaving(true);
      const selectedContractor = contractors.find((c) => c.id === form.contractorId);
      const selectedRate = Number(form.rate || selectedContractor?.perHourRate || 0);
      const hours = Math.max(0, Number(form.hours || 0));
      const computedAmount = Number(
        computeFormAmount(selectedContractor?.payType, form.projectId, hours, selectedRate)
      );
      await graphqlRequest(CREATE_WORK_ENTRY, {
        input: {
          contractorId: form.contractorId,
          projectId: form.projectId,
          date: form.date,
          workDescription: form.workDescription.trim(),
          hours,
          rate: selectedRate,
          amount: computedAmount,
        },
      });
      toast.success("Work entry added.");
      setShowModal(false);
      setForm(EMPTY_FORM);
      loadEntries();
    } catch (err) {
      toast.error(err?.message || "Failed to add work entry.");
    } finally {
      setSaving(false);
    }
  };

  const handleApprove = async (entry) => {
    if (!entry?.id || entry.isTimesheet || updatingEntryId) return;
    try {
      setUpdatingEntryId(entry.id);
      await graphqlRequest(APPROVE_WORK_ENTRY, { id: entry.id });
      setEntries((prev) => prev.map((row) => (
        row.id === entry.id ? { ...row, status: "approved" } : row
      )));
      toast.success("Work entry approved.");
    } catch (err) {
      toast.error(err?.message || "Failed to approve work entry.");
    } finally {
      setUpdatingEntryId("");
    }
  };

  const handleDelete = async (entry) => {
    if (!entry?.id || entry.isTimesheet || updatingEntryId) return;
    setDeleteTarget(entry);
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget?.id || updatingEntryId) return;
    try {
      setUpdatingEntryId(deleteTarget.id);
      await graphqlRequest(REMOVE_WORK_ENTRY, { id: deleteTarget.id });
      setEntries((prev) => prev.filter((row) => row.id !== deleteTarget.id));
      toast.success("Work entry deleted.");
      setDeleteTarget(null);
    } catch (err) {
      toast.error(err?.message || "Failed to delete work entry.");
    } finally {
      setUpdatingEntryId("");
    }
  };

  const handleMarkDraft = async (entry) => {
    if (!entry?.id || entry.isTimesheet || updatingEntryId) return;
    try {
      setUpdatingEntryId(entry.id);
      await graphqlRequest(DRAFT_WORK_ENTRY, { id: entry.id });
      setEntries((prev) => prev.map((row) => (
        row.id === entry.id ? { ...row, status: "draft" } : row
      )));
      toast.success("Work entry moved to draft.");
    } catch (err) {
      const msg = String(err?.message || "");
      if (msg.includes('Cannot query field "draftContractorWorkEntry"')) {
        // Backward-compatible fallback for older backend schema.
        setEntries((prev) => prev.map((row) => (
          row.id === entry.id ? { ...row, status: "draft" } : row
        )));
        toast.warning("Draft action applied in UI. Restart backend to enable DB update for this action.");
      } else {
        toast.error(err?.message || "Failed to mark work entry as draft.");
      }
    } finally {
      setUpdatingEntryId("");
    }
  };

  return (
    <>
      <div className="contractor-payroll-toolbar">
        <div className="toolbar-title">
          <h2>Work Tracking</h2>
          <p>Record billable contractor work</p>
        </div>
        <button className="wt-add-btn" type="button" onClick={() => { setForm(EMPTY_FORM); setShowModal(true); }}>
          + Add Work Entry
        </button>
      </div>

      {showModal && (
        <div className="wt-inline-form-card">
          <div className="wt-inline-form-header"><h3>New Work Entry</h3></div>
          <div className="wt-inline-form-body">
            <div className="wt-form-row">
              <div className="wt-form-group">
                <label>Date</label>
                <input type="date" value={form.date} onChange={(e) => handleFormChange("date", e.target.value)} />
              </div>
              <div className="wt-form-group">
                <label>Contractor</label>
                <select value={form.contractorId} onChange={(e) => handleFormChange("contractorId", e.target.value)}>
                  <option value="">Select contractor</option>
                  {contractors.map((c) => (
                    <option key={c.id} value={c.id}>{c.firstName} {c.lastName}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="wt-form-row">
              <div className="wt-form-group">
                <label>Project</label>
                <select value={form.projectId} onChange={(e) => handleFormChange("projectId", e.target.value)}>
                  <option value="">Select project</option>
                  {projects.map((p) => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
              </div>
              <div className="wt-form-group">
                <label>Work Type</label>
                <input type="text" placeholder="e.g., Deep cleaning" value={form.workDescription} onChange={(e) => handleFormChange("workDescription", e.target.value)} />
              </div>
            </div>
            <div className="wt-form-row">
              <div className="wt-form-group wt-input-with-suffix">
                <label>Hours</label>
                <div className="wt-input-wrapper">
                  <input type="number" min="0" step="0.5" value={form.hours} onChange={(e) => handleFormChange("hours", e.target.value)} />
                  <span className="wt-suffix">h</span>
                </div>
              </div>
              <div className="wt-form-group wt-input-with-prefix">
                <label>Rate ($/hr)</label>
                <div className="wt-input-wrapper">
                  <span className="wt-prefix">$</span>
                  <input type="number" min="0" step="0.01" value={form.rate} onChange={(e) => handleFormChange("rate", e.target.value)} />
                </div>
              </div>
            </div>
            <div className="wt-form-row">
              <div className="wt-form-group wt-input-with-prefix">
                <label>Total Amount</label>
                <div className="wt-input-wrapper">
                  <span className="wt-prefix">$</span>
                  <input type="number" min="0" step="0.01" value={form.amount} onChange={(e) => handleFormChange("amount", e.target.value)} />
                </div>
              </div>
            </div>
          </div>
          <div className="wt-inline-form-footer">
            <button className="wt-save-btn" onClick={handleSave} disabled={saving}>{saving ? "Saving..." : "Save Entry"}</button>
            <button className="wt-cancel-btn" onClick={() => setShowModal(false)}>Cancel</button>
          </div>
        </div>
      )}

      <div className="wt-filters-card">
        <div className="wt-filters-title">Filters</div>
        <div className="wt-filters-bar">
        <div className="wt-filter-group">
          <label>Date From</label>
          <input type="date" value={filters.startDate} onChange={(e) => setFilters((f) => ({ ...f, startDate: e.target.value }))} />
        </div>
        <div className="wt-filter-group">
          <label>Date To</label>
          <input type="date" value={filters.endDate} onChange={(e) => setFilters((f) => ({ ...f, endDate: e.target.value }))} />
        </div>
        <div className="wt-filter-group">
          <label>Select Contractor</label>
          <select value={filters.contractorId} onChange={(e) => setFilters((f) => ({ ...f, contractorId: e.target.value }))}>
            <option value="">All Contractors</option>
            {contractorOptions.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        </div>
        <div className="wt-filter-group">
          <label>Project</label>
          <input type="text" value={filters.projectId} onChange={(e) => setFilters((f) => ({ ...f, projectId: e.target.value }))} />
        </div>
        <button
          className="wt-clear-btn"
          onClick={() => {
            setAppliedFilters({ startDate: filters.startDate || defaultStart, endDate: filters.endDate || defaultEnd });
          }}
        >
          Apply
        </button>
        <button
          className="wt-clear-btn"
          onClick={() => {
            const reset = { startDate: defaultStart, endDate: defaultEnd, contractorId: "", projectId: "" };
            setFilters(reset);
            setAppliedFilters({ startDate: reset.startDate, endDate: reset.endDate });
          }}
        >
          Reset
        </button>
      </div>
      </div>

      {loading && <div className="contractor-payroll-state">Loading work entries...</div>}
      {!loading && error && <div className="contractor-payroll-state error">{error}</div>}

      {!loading && !error && (
        tableEntries.length === 0 ? (
          <div className="contractor-payroll-state">No work entries found.</div>
        ) : (
          <div className="wt-table-wrap">
            <table className="wt-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Contractor</th>
                  <th>Project</th>
                  <th>Work Description</th>
                  <th>Hours</th>
                  <th>Rate</th>
                  <th>Amount</th>
                  {showModal && <th>Status</th>}
                  {showModal && <th>Actions</th>}
                </tr>
              </thead>
              <tbody>
                {tableEntries.map((e) => (
                  <tr key={e.id} className="wt-row">
                    <td>{fmtDate(e.date)}</td>
                    <td><div className="wt-emp-name">{e.contractorName}</div></td>
                    <td>{e.project}</td>
                    <td>{e.workDescription}</td>
                    <td>{Number(e.hours || 0)}h</td>
                    <td>{fmtRate(e.rate, e.payType)}</td>
                    <td className="wt-pay">{fmtCurrency(e.totalAmount)}</td>
                    {showModal && (
                      <td>
                        <span className={`wt-status-badge ${
                          String(e.status).toLowerCase() === "approved"
                            ? "wt-status-approved"
                            : "wt-status-pending"
                        }`}>
                          {String(e.status).toLowerCase() === "approved" ? "Approved" : "Draft"}
                        </span>
                      </td>
                    )}
                    {showModal && (
                      <td>
                        <div className="wt-actions">
                          {e.isTimesheet ? (
                            <>
                              <button
                                className="wt-action-btn approve"
                                type="button"
                                title="Timesheet status is managed from timesheet module"
                                disabled
                              >
                                <FiCheck size={14} />
                              </button>
                              <button
                                className="wt-action-btn delete"
                                type="button"
                                title="Timesheet entry cannot be deleted here"
                                disabled
                              >
                                <FiTrash2 size={14} />
                              </button>
                            </>
                          ) : String(e.status || "").toLowerCase() === "draft" ? (
                            <>
                              <button
                                className="wt-action-btn approve"
                                type="button"
                                title="Approve"
                                disabled={updatingEntryId === e.id}
                                onClick={() => handleApprove(e)}
                              >
                                <FiCheck size={14} />
                              </button>
                              <button
                                className="wt-action-btn delete"
                                type="button"
                                title="Delete"
                                disabled={updatingEntryId === e.id}
                                onClick={() => handleDelete(e)}
                              >
                                <FiTrash2 size={14} />
                              </button>
                            </>
                          ) : (
                            <>
                              <button
                                className="wt-action-btn approve"
                                type="button"
                                title="Move to draft"
                                disabled={updatingEntryId === e.id}
                                onClick={() => handleMarkDraft(e)}
                              >
                                <FiX size={14} />
                              </button>
                              <button
                                className="wt-action-btn delete"
                                type="button"
                                title="Delete"
                                disabled={updatingEntryId === e.id}
                                onClick={() => handleDelete(e)}
                              >
                                <FiTrash2 size={14} />
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}

      {deleteTarget && (
        <div className="wt-modal-overlay" onClick={() => setDeleteTarget(null)}>
          <div className="wt-modal" onClick={(e) => e.stopPropagation()}>
            <div className="wt-modal-header">
              <h3>Delete Work Entry</h3>
              <button className="wt-modal-close" type="button" onClick={() => setDeleteTarget(null)}>
                <FiX size={16} />
              </button>
            </div>
            <div className="wt-modal-body">
              <p style={{ margin: 0, fontSize: 13, color: "#4b5563" }}>
                Are you sure you want to delete this work entry? This action cannot be undone.
              </p>
            </div>
            <div className="wt-modal-footer">
              <button className="wt-save-btn" onClick={handleConfirmDelete} disabled={updatingEntryId === deleteTarget.id}>
                {updatingEntryId === deleteTarget.id ? "Deleting..." : "Delete"}
              </button>
              <button className="wt-cancel-btn" onClick={() => setDeleteTarget(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default WorkTracking;
